import '.a{display:flex;}.ssr-plugin'
import '.g8{aspect-ratio:1/1 !important;}.ssr-plugin'
import '.ek{flex-shrink:0;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.bc{overflow:hidden;}.ssr-plugin'
import '.f{width:100%;}.ssr-plugin'
import '.am{height:100%;}.ssr-plugin'
import '.g9{background-position:center center;}.ssr-plugin'
import '.ga{background-size:cover;}.ssr-plugin'
import '.gb{background-repeat:no-repeat;}.ssr-plugin'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react'
import { styled, Style } from 'inlines'
import { radius } from '../../utils/radius.js'
import { colors } from '../../tokens/colors.js'
import { Loader } from '../Loader/index.js'
import { Icon } from '../Icon/index.js'
import { AudioPreview } from './AudioPreview.js'
import { VideoPreview } from './VideoPreview.js'

const VID_TYPES = new Set([
  'mp4',
  'webm',
  'ogg',
  'mov',
  'wmv',
  'flv',
  'avi',
  'qt',
])
const AUDIO_TYPES = new Set(['mp3', 'wav', 'flac', 'aac'])
const IMG_TYPES = new Set(['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'])
const CODE_FILE_TYPES = new Set([
  'html',
  'js',
  'css',
  'ts',
  'tsx',
  'json',
  'md',
])

const MediaPreviewsContext = createContext<{
  previews: { [key: string]: string }
  addPreview: (id: string, objectURL: string) => void
}>({ previews: {}, addPreview: () => {} })

type ImagePreviewsContextProviderProps = { children: ReactNode }

export function MediaPreviewsContextProvider({
  children,
}: ImagePreviewsContextProviderProps) {
  const [previews, setPreviews] = useState({})

  const addPreview = useCallback((id: string, objectURL: string) => {
    setPreviews((p) => ({ ...p, [id]: objectURL }))
  }, [])

  return (
    <MediaPreviewsContext.Provider value={{ previews, addPreview }}>
      {children}
    </MediaPreviewsContext.Provider>
  )
}

export function useMediaPreviews() {
  return useContext(MediaPreviewsContext)
}

function getOptimizedImageSrc(src: string, width: number) {
  if (
    !src ||
    !src.includes('.based.dev/') ||
    src.includes('cdn-cgi/image/') ||
    src.includes('.ico') ||
    src.startsWith('blob:')
  ) {
    return src
  }

  return src.replace('.based.dev/', `.based.dev/cdn-cgi/image/w=${width}/`)
}

export type MediaProps = {
  src?: string
  type?: 'image' | 'video' | 'file' | 'audio' | 'color' | 'code'
  size?: 'regular' | 'small'
  thumbnail?: string
  loading?: boolean
  optimizedImageWidth?: number
  style?: Style
}

export const Media = ({
  src: source,
  type,
  thumbnail,
  loading,
  size = 'regular',
  optimizedImageWidth = 128,
  style,
}: MediaProps) => {
  const { previews } = useMediaPreviews()
  const [focus, setFocus] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [vidProgress, setVidProgress] = useState(0)
  const mediaRef = useRef<HTMLMediaElement>(null)

  const extension =
    typeof source === 'string'
      ? (source.match(/\.([^./?]+)(?=[?#]|$)/)?.[1]?.toLowerCase() ?? '')
      : ''

  if (source && !type) {
    if (VID_TYPES.has(extension)) {
      type = 'video'
    } else if (CODE_FILE_TYPES.has(extension)) {
      type = 'code'
    } else if (AUDIO_TYPES.has(extension)) {
      type = 'audio'
    } else if (IMG_TYPES.has(extension)) {
      type = 'image'
    } else if (
      typeof source === 'string' &&
      (source.includes('#') ||
        source.startsWith('rgb') ||
        source.startsWith('hsl'))
    ) {
      type = 'color'
    } else {
      type = 'file'
    }
  }

  useEffect(() => {
    const media = mediaRef.current
    if (!media || !source) {
      return
    }

    media.src = source

    const start = () => setIsPlaying(true)
    const stop = () => setIsPlaying(false)
    const update = () =>
      setVidProgress((media.currentTime / media.duration) * 100)

    media.addEventListener('timeupdate', update)
    media.addEventListener('play', start)
    media.addEventListener('pause', stop)
    media.addEventListener('ended', stop)

    return () => {
      media.removeEventListener('timeupdate', update)
      media.removeEventListener('play', start)
      media.removeEventListener('pause', stop)
      media.removeEventListener('ended', stop)
    }
  }, [source, extension])

  const src = useMemo(() => {
    if (previews[source]) {
      return previews[source]
    }
    if (type === 'image') {
      return getOptimizedImageSrc(source, optimizedImageWidth)
    }
    return source
  }, [source, optimizedImageWidth, previews])

  const isRegular = size === 'regular'
  const divSize = isRegular ? 72 : 24
  const iconSize = loading
    ? isRegular
      ? '18px'
      : '12px'
    : isRegular
      ? '24px'
      : '16px'

  return (
    <styled.div className="a g8 ek c d bc"
      tabIndex={0}
      onFocus={() => {
        setFocus(true)
      }}
      onBlur={() => {
        setFocus(false)
      }}
      style={{
        color: colors.neutral20,
        width: divSize,
        height: divSize,
        borderRadius: isRegular ? radius[12] : radius[6],
        border: `1px solid ${focus ? colors.neutral100 : colors.neutral20Adjusted}`,
        backgroundColor: type === 'color' ? source : colors.neutral10Adjusted,
        outline: focus ? `4px solid ${colors.neutral20}` : null,
        '& svg': {
          width: iconSize,
          height: iconSize,
        },
        ...style,
      }}
    >
      {loading ? (
        <Loader />
      ) : type === 'file' ? (
        <Icon variant="page" />
      ) : type === 'code' ? (
        <Icon variant="code-on" />
      ) : type === 'image' && !src ? (
        <Icon variant="image" />
      ) : type === 'video' && !src ? (
        <Icon variant="video" />
      ) : type === 'audio' && !src ? (
        <Icon variant="music" />
      ) : type === 'image' && src ? (
        <div className="f am g9 ga gb"
          style={{
            backgroundImage: `url(${src})`,}}
        />
      ) : type === 'video' ? (
        <VideoPreview src={src} poster={thumbnail} />
      ) : type === 'audio' ? (
        <AudioPreview
          isPlaying={isPlaying}
          size={size}
          vidProgress={vidProgress}
          setVidProgress={setVidProgress}
          src={src}
          ref={mediaRef as React.RefObject<HTMLAudioElement>}
        />
      ) : null}
    </styled.div>
  )
}
